import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import { API_BASE_URL, fetchJson } from './api';
import DataIntroductionProposal, { proposalTitle } from './DataIntroductionProposal';

const CONFIG = {
    email: {
        title: 'Email Search',
        intro: 'Review the exact reference text and AI-inferred detail before accepting or rejecting. Acceptance retains a received email statement; it is not independent factual verification.',
        action: 'Search email'
    },
    ai_review: {
        title: 'AI Review',
        intro: 'Project-bound planning and benchmark proposals. Extraction acceptance does not confirm factual accuracy.',
        action: 'Run review',
        accept: ''
    },
    data_pack: {
        title: 'Data Pack Upload',
        intro: 'Upload the Initial Report data pack. Source files are retained and extracted facts are presented for approval.',
        action: 'Upload and review',
        accept: '.pdf,.docx,.xlsx,.xls,.csv,.txt,.jpg,.jpeg,.png,.webp',
        multiple: true
    },
    soi: {
        title: 'SOI Upload',
        intro: 'Review each Schedule of Information question against its extracted response and source. Accept keeps the response for report data; Reject excludes it. Acceptance does not verify factual accuracy.',
        action: 'Upload and review',
        accept: '.xlsx,.xls,.pdf,.docx,.txt,.jpg,.jpeg,.png,.webp',
        multiple: true
    },
    notes: {
        title: 'Notes Upload',
        intro: 'Upload or paste surveyor notes for this Interim Report. Notes remain reviewable before entering report data.',
        action: 'Introduce notes',
        accept: '.pdf,.docx,.txt,.csv',
        multiple: true
    },
    voice: {
        title: 'Voice Transcription',
        intro: 'Record site or valuation notes. The source audio and transcript are retained, while reportable facts are rewritten professionally for individual approval.',
        action: 'Upload recording',
        accept: 'audio/webm,audio/ogg,audio/mpeg,audio/mp4,.m4a,.wav'
    }
};

function authHeaders(extra = {}) {
    let token = '';
    try { token = String(localStorage.getItem('monsurv_auth_token') || '').trim(); } catch { }
    return token ? { ...extra, Authorization: `Bearer ${token}` } : extra;
}

function uploadFile(upload, file, onProgress) {
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open(upload.method || 'PUT', upload.url, true);
        Object.entries(upload.headers || {}).forEach(([name, value]) => request.setRequestHeader(name, value));
        request.upload.onprogress = (event) => {
            if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
        };
        request.onerror = () => reject(new Error('The upload could not reach document storage.'));
        request.onload = () => {
            if (request.status >= 200 && request.status < 300) resolve();
            else reject(new Error(`Document storage rejected the upload (HTTP ${request.status}).`));
        };
        request.send(file);
    });
}

function emailProgressLabel(progress) {
    if (progress?.stage === 'connecting') return 'Connecting to Gmail and finding matching emails...';
    if (progress?.stage === 'reading') return `Reading emails: ${progress.messages_processed || 0} of ${progress.messages_total || 0}; ${progress.messages_used || 0} project emails matched.`;
    if (progress?.stage === 'extracting') return `Extracting email details: ${progress.extraction_batches_completed || 0} of ${progress.extraction_batches_total || 0} AI batches complete; ${progress.details_found || 0} details found.`;
    if (progress?.stage === 'mapping') return `Mapping details to report fields: ${progress.mapping_completed || 0} of ${progress.mapping_total || 0} details processed (${progress.mapping_batches_completed || 0} of ${progress.mapping_batches_total || 0} AI batches).`;
    if (progress?.stage === 'saving') return 'Saving email details for Accept/Reject review...';
    return 'Searching Gmail and preparing grounded email details...';
}

export default function DataIntroductionDialog({ open, onClose, onProposalReviewed, reference, reportInstance, intakeType }) {
    const config = CONFIG[intakeType] || CONFIG.ai_review;
    const inputRef = useRef(null);
    const recorderRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const audioChunksRef = useRef([]);
    const [files, setFiles] = useState([]);
    const [notesText, setNotesText] = useState('');
    const [voiceConsent, setVoiceConsent] = useState(false);
    const [recording, setRecording] = useState(false);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState('');
    const [run, setRun] = useState(null);
    const [reviewingId, setReviewingId] = useState(null);
    const [authorRole, setAuthorRole] = useState('unknown');
    const [sourceDate, setSourceDate] = useState('');
    const [soiBindings, setSoiBindings] = useState({});
    const [emailContext, setEmailContext] = useState(null);
    const [emailContextLoading, setEmailContextLoading] = useState(false);
    const [emailConsent, setEmailConsent] = useState(false);
    const [emailProgress, setEmailProgress] = useState(null);

    useEffect(() => {
        if (!open || intakeType !== 'email') return;
        let active = true;
        setEmailContext(null); setEmailConsent(false); setEmailContextLoading(true); setError('');
        fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(reference)}/email-search-context?mode=surveyor&report_instance=${encodeURIComponent(reportInstance)}`)
            .then((context) => { if (active) setEmailContext(context); })
            .catch((failure) => { if (active) setError(failure.message || 'Email search could not be prepared.'); })
            .finally(() => { if (active) setEmailContextLoading(false); });
        return () => { active = false; };
    }, [open, intakeType, reference, reportInstance]);

    useEffect(() => {
        if (open) return;
        setFiles([]);
        setNotesText('');
        setVoiceConsent(false);
        setRecording(false);
        setBusy(false);
        setProgress(0);
        setError('');
        setRun(null);
        setAuthorRole('unknown'); setSourceDate('');
        setSoiBindings({});
        setEmailConsent(false); setEmailContext(null);
        setEmailProgress(null);
        if (mediaStreamRef.current) mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
        recorderRef.current = null;
    }, [open]);

    async function startRecording() {
        setError('');
        if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
            setError('Voice recording is not supported by this browser.');
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream);
            mediaStreamRef.current = stream;
            recorderRef.current = recorder;
            audioChunksRef.current = [];
            recorder.ondataavailable = (event) => {
                if (event.data?.size) audioChunksRef.current.push(event.data);
            };
            recorder.onstop = () => {
                const contentType = recorder.mimeType || 'audio/webm';
                const extension = contentType.includes('ogg') ? 'ogg' : contentType.includes('mp4') ? 'm4a' : 'webm';
                const blob = new Blob(audioChunksRef.current, { type: contentType });
                setFiles([new File([blob], `voice-note-${Date.now()}.${extension}`, { type: contentType })]);
                stream.getTracks().forEach((track) => track.stop());
                mediaStreamRef.current = null;
            };
            recorder.start();
            setRecording(true);
        } catch (recordingError) {
            setError(recordingError?.message || 'Microphone access was not granted.');
        }
    }

    function stopRecording() {
        if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
        setRecording(false);
    }

    async function createAndProcess() {
        setError('');
        const selectedFiles = [...files];
        if (intakeType === 'notes' && notesText.trim()) {
            selectedFiles.push(new File([notesText.trim()], `interim-notes-${Date.now()}.txt`, { type: 'text/plain' }));
        }
        if (!['ai_review', 'email'].includes(intakeType) && selectedFiles.length === 0) {
            setError('Choose a source file or add notes first.');
            return;
        }
        if (intakeType === 'voice' && !voiceConsent) {
            setError('Confirm consent to retain and transcribe the recording.');
            return;
        }
        if (intakeType === 'email' && (!emailConsent || !emailContext?.connection?.configured || !emailContext?.connection?.connected)) {
            setError('Connect Gmail and confirm consent before searching email.');
            return;
        }

        setBusy(true);
        setProgress(0);
        setEmailProgress(null);
        try {
            const base = `${API_BASE_URL}/project/${encodeURIComponent(reference)}/data-intakes`;
            const created = await fetchJson(`${base}?mode=surveyor`, {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ report_instance: reportInstance, intake_type: intakeType,
                    settings: { ai_processing_consent: intakeType === 'email' && emailConsent,
                        author_role: authorRole, source_date: sourceDate || null,
                        soi_attachment_bindings: Object.entries(soiBindings).filter(([, row]) => row.trim())
                            .map(([file_name, row]) => ({ file_name, soi_row_id: `SOI-${row.replace(/^SOI-/i, '').toUpperCase()}` })) } })
            });
            const runId = created.run.id;
            for (let index = 0; index < selectedFiles.length; index += 1) {
                const file = selectedFiles[index];
                const intent = await fetchJson(`${base}/${runId}/upload-intents?mode=surveyor`, {
                    method: 'POST',
                    headers: authHeaders({ 'Content-Type': 'application/json' }),
                    body: JSON.stringify({
                        original_file_name: file.name,
                        content_type: file.type || 'application/octet-stream',
                        size_bytes: file.size,
                        ai_processing_consent: intakeType === 'voice' ? voiceConsent : true
                    })
                });
                await uploadFile(intent.upload, file, (fileProgress) => {
                    setProgress(Math.round(((index + (fileProgress / 100)) / selectedFiles.length) * 80));
                });
                await fetchJson(`${base}/${runId}/files/${intent.file.id}/complete?mode=surveyor`, {
                    method: 'POST',
                    headers: authHeaders()
                });
            }
            setProgress(90);
            let processed = await fetchJson(`${base}/${runId}/process?mode=surveyor`, {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ ai_processing_consent: intakeType === 'email' && emailConsent })
            });
            if (intakeType === 'email') {
                const deadline = Date.now() + 15 * 60 * 1000;
                setEmailProgress(processed.run.result_summary?.progress || null);
                while (processed.run.status === 'processing') {
                    if (Date.now() > deadline) throw new Error('Email search has not finished after 15 minutes. Start a new search if the backend was restarted.');
                    await new Promise((resolve) => setTimeout(resolve, 2000));
                    processed = await fetchJson(`${base}/${runId}?mode=surveyor`, { headers: authHeaders() });
                    setEmailProgress(processed.run.result_summary?.progress || null);
                }
                if (processed.run.status === 'failed') throw new Error(processed.run.result_summary?.error || 'Email search failed. Please try again.');
            }
            setRun(processed.run);
            setProgress(100);
        } catch (processingError) {
            setError(processingError?.message || 'Data introduction failed.');
        } finally {
            setBusy(false);
        }
    }

    async function reviewProposal(proposalId, reviewStatus) {
        setReviewingId(proposalId);
        setError('');
        try {
            const url = `${API_BASE_URL}/project/${encodeURIComponent(reference)}/data-intakes/proposals/${proposalId}?mode=surveyor`;
            const result = await fetchJson(url, {
                method: 'PATCH',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ review_status: reviewStatus })
            });
            setRun((current) => ({
                ...current,
                proposals: (current?.proposals || []).map((proposal) => (
                    proposal.id === proposalId ? result.proposal : proposal
                ))
            }));
            if (typeof onProposalReviewed === 'function') onProposalReviewed(result.proposal);
        } catch (reviewError) {
            setError(reviewError?.message || 'The proposal decision could not be saved.');
        } finally {
            setReviewingId(null);
        }
    }

    const warnings = Array.isArray(run?.result_summary?.warnings) ? run.result_summary.warnings : [];
    const proposals = Array.isArray(run?.proposals) ? run.proposals : [];
    const canClose = !busy && !recording && reviewingId == null;

    return (
        <Dialog open={open} onClose={canClose ? onClose : undefined} fullWidth maxWidth="md">
            <DialogTitle>{config.title}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>{config.intro}</Typography>
                {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
                {warnings.map((warning) => <Alert key={warning} severity="warning" sx={{ mb: 1 }}>{warning}</Alert>)}

                {!run && intakeType === 'email' ? <Stack spacing={1.5}>
                    {emailContextLoading ? <CircularProgress size={22} aria-label="Preparing email search" /> : null}
                    {emailContext ? <>
                        <TextField label="Project email search request" value={emailContext.prompt} multiline fullWidth InputProps={{ readOnly: true }} />
                        <Typography variant="body2">Search up to 50 matching received inbox emails from the last 12 months. Quoted email history and attachments are excluded. This is a snapshot, not continuous inbox monitoring.</Typography>
                        {emailContext.connection.connected ? <Typography variant="body2">Connected inbox: {emailContext.connection.email_address}</Typography> : <Alert severity="warning">Connect Gmail in Settings &gt; Integrations before searching.</Alert>}
                        {!emailContext.connection.configured ? <Alert severity="warning">Gmail requires administrator configuration before it can be used.</Alert> : null}
                        <Button href="?view=settings&settings_category=integrations" variant="text" sx={{ alignSelf: 'flex-start' }}>Open Gmail settings</Button>
                        <FormControlLabel control={<Checkbox checked={emailConsent} onChange={(event) => setEmailConsent(event.target.checked)}
                            disabled={busy || !emailContext.connection.connected || !emailContext.connection.configured} />}
                            label="I consent to send matching email text to the configured AI provider and retain excerpts and inferred details in this project, visible to users with project access." />
                    </> : null}
                </Stack> : null}

                {!run && !['ai_review', 'email'].includes(intakeType) ? (
                    <Stack spacing={1.5}>
                        {['notes', 'voice'].includes(intakeType) ? (
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                <TextField select fullWidth size="small" label="Declared author role" value={authorRole} onChange={(event) => setAuthorRole(event.target.value)}>
                                    {['unknown', 'surveyor', 'borrower', 'contractor', 'consultant', 'multiple_speakers'].map((role) => <MenuItem key={role} value={role}>{role.replace(/_/g, ' ')}</MenuItem>)}
                                </TextField>
                                <TextField fullWidth size="small" type="date" label="Source / visit date" InputLabelProps={{ shrink: true }} value={sourceDate} onChange={(event) => setSourceDate(event.target.value)} />
                            </Stack>
                        ) : null}
                        {intakeType === 'voice' ? (
                            <>
                                <Stack direction="row" spacing={1}>
                                    <Button
                                        variant={recording ? 'contained' : 'outlined'}
                                        color={recording ? 'error' : 'primary'}
                                        startIcon={recording ? <StopRoundedIcon /> : <MicRoundedIcon />}
                                        onClick={recording ? stopRecording : startRecording}
                                        disabled={busy}
                                    >
                                        {recording ? 'Stop recording' : 'Start recording'}
                                    </Button>
                                    <Button variant="outlined" startIcon={<UploadFileRoundedIcon />} onClick={() => inputRef.current?.click()} disabled={busy || recording}>
                                        Choose audio
                                    </Button>
                                </Stack>
                                <FormControlLabel
                                    control={<Checkbox checked={voiceConsent} onChange={(event) => setVoiceConsent(event.target.checked)} />}
                                    label="I have consent to retain this audio and send it for transcription."
                                />
                            </>
                        ) : (
                            <Button variant="outlined" startIcon={<UploadFileRoundedIcon />} onClick={() => inputRef.current?.click()} disabled={busy}>
                                Choose {config.multiple ? 'files' : 'file'}
                            </Button>
                        )}
                        <input
                            ref={inputRef}
                            hidden
                            type="file"
                            accept={config.accept}
                            multiple={Boolean(config.multiple)}
                            onChange={(event) => setFiles(Array.from(event.target.files || []))}
                        />
                        {files.length ? (
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                {files.map((file) => <Chip key={`${file.name}-${file.size}`} label={file.name} />)}
                            </Stack>
                        ) : null}
                        {intakeType === 'soi' ? files.filter((file) => !/\.xlsx?$/i.test(file.name)).map((file) => (
                            <TextField key={`${file.name}-soi`} fullWidth size="small" label={`SOI response cell for ${file.name}`}
                                value={soiBindings[file.name] || ''} placeholder="B63" onChange={(event) => setSoiBindings((previous) => ({ ...previous, [file.name]: event.target.value }))} />
                        )) : null}
                        {intakeType === 'notes' ? (
                            <TextField
                                label="Paste notes"
                                value={notesText}
                                onChange={(event) => setNotesText(event.target.value)}
                                multiline
                                minRows={5}
                                fullWidth
                            />
                        ) : null}
                    </Stack>
                ) : null}

                {busy ? (
                    <Box sx={{ mt: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <CircularProgress size={18} />
                            <Typography variant="body2" role="status" aria-live="polite">{intakeType === 'email' ? emailProgressLabel(emailProgress) : 'Uploading and preparing proposals...'}</Typography>
                        </Stack>
                        <LinearProgress variant={intakeType === 'email' ? 'indeterminate' : 'determinate'} value={progress} />
                    </Box>
                ) : null}

                {run ? (
                    <Stack spacing={1.25} sx={{ mt: 1 }}>
                        {intakeType === 'email' && run.result_summary ? <Alert severity="info">
                            {run.result_summary.messages_inspected || 0} emails inspected; {run.result_summary.messages_used || 0} matched project emails used.
                            {' '}Snapshot for {run.result_summary.report_label}. Attachments were not processed.
                        </Alert> : null}
                        {intakeType === 'email' && run.result_summary?.reconciliation_coverage?.status === 'partial' ? <Alert severity="warning">
                            Some email details could not be fully mapped to report fields. Review the source excerpts carefully; report generation may require resolving these processing gaps.
                        </Alert> : null}
                        {run.result_summary?.coverage ? <Alert severity={run.result_summary.coverage.status === 'complete' ? 'info' : 'warning'}>
                            {run.result_summary.coverage.complete} of {run.result_summary.coverage.total} files fully processed; {run.result_summary.coverage.gaps?.length || 0} coverage gaps. Factual review remains separate.
                        </Alert> : null}
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CheckCircleRoundedIcon color="success" />
                            <Typography sx={{ fontWeight: 800 }}>{proposals.length} proposal{proposals.length === 1 ? '' : 's'} ready</Typography>
                        </Stack>
                        {!proposals.length ? <Alert severity="info">No supported facts were found in the available evidence.</Alert> : null}
                        {proposals.map((proposal) => (
                            <Paper key={proposal.id} component="article" aria-label={proposalTitle(proposal)} variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
                                            <Typography sx={{ fontWeight: 850 }}>{proposalTitle(proposal)}</Typography>
                                            {proposal.confidence != null ? <Chip size="small" label={`${Math.round(Number(proposal.confidence) * 100)}% extraction confidence`} /> : null}
                                            {proposal.review_status !== 'pending' ? <Chip size="small" color={proposal.review_status === 'accepted' ? 'success' : 'default'} label={proposal.review_status} /> : null}
                                        </Stack>
                                        {proposal.category !== 'soi_mapping' ? <Typography variant="body2" sx={{ mt: 0.75 }}>{proposal.rationale}</Typography> : null}
                                        <DataIntroductionProposal proposal={proposal} />
                                    </Box>
                                    {proposal.review_status === 'pending' ? (
                                        <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0, alignSelf: 'flex-start' }}>
                                            <Button size="small" color="success" variant="contained" disabled={reviewingId === proposal.id} onClick={() => reviewProposal(proposal.id, 'accepted')}>Accept</Button>
                                            <Button size="small" color="inherit" variant="outlined" disabled={reviewingId === proposal.id} onClick={() => reviewProposal(proposal.id, 'rejected')}>Reject</Button>
                                        </Stack>
                                    ) : null}
                                </Stack>
                            </Paper>
                        ))}
                    </Stack>
                ) : null}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={!canClose}>Close</Button>
                {!run ? <Button variant="contained" onClick={createAndProcess} disabled={busy || recording
                    || (intakeType === 'email' && (emailContextLoading || !emailConsent || !emailContext?.connection?.connected || !emailContext?.connection?.configured))}>{config.action}</Button> : null}
            </DialogActions>
        </Dialog>
    );
}